/* ==================================================
  カスタム:商品詳細のサムネイル操作

  サムネイルの横並びはブラウザ標準のスクロール領域のため、
  タッチのスワイプでは動くがマウスのドラッグでは動かない。
  さらに画像・リンクの標準ドラッグ（ゴースト表示）が始まってしまい
  「掴んでも動かない」状態になるため、ドラッグでのスクロールを足す
===================================================*/

(() => {
  const STRIP = '.c_product-template__thumbs';

  /*  この距離を超えて動いたらクリックではなくドラッグとみなす  */
  const DRAG_THRESHOLD = 5;

  /* ------------------------------
    ドラッグでの横スクロール
  ------------------------------*/

  const init = (strip) => {
    if (strip.dataset.c_dragReady === 'true') return;
    strip.dataset.c_dragReady = 'true';

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let suppressClick = false;

    /*  画像・リンクの標準ドラッグを止める  */
    strip.querySelectorAll('a, img').forEach((el) => {
      el.draggable = false;
    });

    strip.addEventListener('pointerdown', (event) => {
      /*  タッチとペンは端末標準のスクロール（慣性つき）に任せる  */
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = strip.scrollLeft;
      dragging = false;
      suppressClick = false;
    });

    strip.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const distance = event.clientX - startX;

      if (!dragging) {
        if (Math.abs(distance) < DRAG_THRESHOLD) return;
        dragging = true;
        strip.classList.add('is-dragging');

        /*  捕捉できない環境でもスクロール自体は動かす  */
        try {
          strip.setPointerCapture(pointerId);
        } catch {
          /*  握れなくても続行する  */
        }
      }

      strip.scrollLeft = startScroll - distance;
      event.preventDefault();
    });

    const finish = (event) => {
      if (event.pointerId !== pointerId) return;

      try {
        if (strip.hasPointerCapture(pointerId)) {
          strip.releasePointerCapture(pointerId);
        }
      } catch {
        /*  捕捉していなければ何もしない  */
      }

      /*  ドラッグして指を離した直後のクリックで画像が切り替わらないようにする  */
      suppressClick = dragging;
      dragging = false;
      pointerId = null;
      strip.classList.remove('is-dragging');
    };

    strip.addEventListener('pointerup', finish);
    strip.addEventListener('pointercancel', finish);

    /*  capture で拾ってテーマ本体の画像切替より先に握りつぶす  */
    strip.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  };

  /* ------------------------------
    初期化
  ------------------------------*/

  const initAll = (scope) => {
    scope.querySelectorAll(STRIP).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  /*  テーマエディタでセクションを操作したとき  */
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();

/* ==================================================
  カスタム:商品詳細のメイン画像スライド

  表示中の画像はテーマ本体（theme.js）が .hide の付け外しで決めている。
  切替の主導権はテーマ本体に残したまま、見た目だけ横スライドにする:
  - 前へ/次へは対応するサムネイルをクリックしてテーマ本体に切替えさせる
  - .hide の変化を MutationObserver で拾い、そこからアニメーションする
  こうするとサムネイル・バリアント変更・矢印のどれで切り替えても同じ動きになる
===================================================*/

(() => {
  const GALLERY = '[data-c_pt-slider]';
  const PHOTOS = '.c_product-template__photos';
  const SLIDE = '.product-single__photo-wrapper';
  const PHOTO = '.product-single__photo';

  /*  この距離を超えて横に動いたらスワイプとみなす  */
  const SWIPE_THRESHOLD = 40;

  const initGallery = (gallery) => {
    if (gallery.dataset.c_sliderReady === 'true') return;

    const photos = gallery.querySelector(PHOTOS);
    if (!photos) return;

    const slides = Array.from(photos.querySelectorAll(SLIDE));
    if (slides.length < 2) return;

    gallery.dataset.c_sliderReady = 'true';

    const section = gallery.closest('[data-section-id]');
    const photoOf = (slide) => slide.querySelector(PHOTO);

    /*  テーマ本体が .hide を外した1点が表示中  */
    const activeIndex = () => {
      const found = slides.findIndex((slide) => {
        const photo = photoOf(slide);
        return photo && !photo.classList.contains('hide');
      });
      return found < 0 ? 0 : found;
    };

    let index = activeIndex();
    let pendingDir = 0;
    let parkTimer = null;
    let suppressClick = false;

    const duration = () => {
      const value = getComputedStyle(photos).transitionDuration;
      return (parseFloat(value) || 0) * 1000;
    };

    /*  アニメーションさせずにスタイルを当てる  */
    const withoutAnimation = (apply) => {
      photos.classList.add('is-no-anim');
      apply();
      void photos.offsetWidth;
      photos.classList.remove('is-no-anim');
    };

    /*  表示していないスライドを右側に片付ける  */
    const park = (slide) => {
      slide.setAttribute('aria-hidden', 'true');
      slide.style.transform = 'translateX(100%)';
    };

    const setHeight = () => {
      photos.style.height = `${slides[index].offsetHeight}px`;
    };

    const render = (next) => {
      const dir = pendingDir || (next > index ? 1 : -1);
      pendingDir = 0;

      const from = slides[index];
      const to = slides[next];

      /*  入ってくる側を進行方向の外側に置いてから中央へ動かす  */
      withoutAnimation(() => {
        to.setAttribute('aria-hidden', 'false');
        to.style.transform = `translateX(${dir * 100}%)`;
      });

      to.style.transform = 'translateX(0)';
      from.style.transform = `translateX(${dir * -100}%)`;

      /*  比率の違う画像に切り替わっても飛ばないよう、高さも一緒に動かす  */
      photos.style.height = `${to.offsetHeight}px`;

      index = next;

      /*  動き終わってから隠す。途中で隠すと出ていく画像が消えてしまう  */
      window.clearTimeout(parkTimer);
      parkTimer = window.setTimeout(() => {
        withoutAnimation(() => {
          slides.forEach((slide, i) => {
            if (i !== index) park(slide);
          });
        });
      }, duration() + 30);
    };

    /* ------------------------------
      前へ/次へ
    ------------------------------*/

    const thumbFor = (slide) => {
      const photo = photoOf(slide);
      const imageId = photo && photo.getAttribute('data-image-id');
      if (!imageId || !section) return null;
      return section.querySelector(`[data-thumbnail-id="${imageId}"]`);
    };

    const goTo = (target, dir) => {
      const next = (target + slides.length) % slides.length;
      if (next === index) return;

      pendingDir = dir;

      const thumb = thumbFor(slides[next]);
      if (thumb) thumb.click();

      /*  テーマ本体が動いていない環境でも切り替わるようにする  */
      if (activeIndex() !== next) {
        slides.forEach((slide, i) => {
          const photo = photoOf(slide);
          if (photo) photo.classList.toggle('hide', i !== next);
        });
      }
    };

    const step = (dir) => goTo(index + dir, dir);

    /* ------------------------------
      初期化と操作
    ------------------------------*/

    photos.classList.add('is-slider');

    withoutAnimation(() => {
      slides.forEach((slide, i) => {
        if (i === index) {
          slide.setAttribute('aria-hidden', 'false');
          slide.style.transform = 'translateX(0)';
        } else {
          park(slide);
        }
      });
      setHeight();
    });

    /*  画像の左半分で前へ、右半分で次へ  */
    photos.addEventListener('click', (event) => {
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        return;
      }

      const rect = photos.getBoundingClientRect();
      const isPrev = event.clientX - rect.left < rect.width / 2;
      step(isPrev ? -1 : 1);
    });

    gallery.querySelectorAll('[data-c_pt-nav]').forEach((button) => {
      button.addEventListener('click', () => {
        step(button.getAttribute('data-c_pt-nav') === 'prev' ? -1 : 1);
      });
    });

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    });

    /*  タッチのスワイプ  */
    let swipeId = null;
    let swipeX = 0;
    let swipeY = 0;

    photos.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      swipeId = event.pointerId;
      swipeX = event.clientX;
      swipeY = event.clientY;
    });

    const endSwipe = (event) => {
      if (event.pointerId !== swipeId) return;
      swipeId = null;

      const dx = event.clientX - swipeX;
      const dy = event.clientY - swipeY;

      /*  縦スクロールの途中で反応しないよう、横の動きが勝っているときだけ  */
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

      suppressClick = true;
      step(dx > 0 ? -1 : 1);
    };

    photos.addEventListener('pointerup', endSwipe);
    photos.addEventListener('pointercancel', () => {
      swipeId = null;
    });

    /*  幅が変わると比率から決まる高さも変わる  */
    let resizeFrame = null;
    window.addEventListener('resize', () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        withoutAnimation(setHeight);
      });
    });

    /*  サムネイルやバリアント変更でテーマ本体が切り替えたときも同じ動きにする  */
    const observer = new MutationObserver(() => {
      const next = activeIndex();
      if (next !== index) render(next);
    });

    slides.forEach((slide) => {
      const photo = photoOf(slide);
      if (photo) observer.observe(photo, { attributes: true, attributeFilter: ['class'] });
    });
  };

  const initGalleries = (scope) => {
    scope.querySelectorAll(GALLERY).forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initGalleries(document));
  } else {
    initGalleries(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initGalleries(event.target);
  });
})();

/* ==================================================
  カスタム:商品詳細の税込価格の維持

  表示価格は snippets/sagan-product-price.liquid が税込倍率を掛けて出している。
  一方でバリアントを切り替えるとテーマ本体(theme.js)が商品JSONの素の価格で
  上書きしてしまい、リロードするまで金額が合わない。
  テーマ本体が書き換えた直後に、同じ倍率を掛け直して整合させる
===================================================*/

(() => {
  const SECTION = '[data-section-type="product"]';

  const initPrice = (container) => {
    if (container.dataset.c_priceReady === 'true') return;

    const priceEl = container.querySelector('[data-price]');
    if (!priceEl) return;

    const factor = parseFloat(priceEl.getAttribute('data-tax-factor'));
    if (!factor || factor === 1) return;

    /*  金額の書式はテーマ本体の実装に合わせる  */
    const currency = window.theme && window.theme.Currency;
    if (!currency || typeof currency.formatMoney !== 'function') return;

    const sectionId = container.getAttribute('data-section-id');
    const json = document.getElementById(`ProductJson-${sectionId}`);
    if (!json) return;

    let productJson = null;
    try {
      productJson = JSON.parse(json.textContent);
    } catch {
      return;
    }
    if (!productJson || !productJson.variants) return;

    const idInput = container.querySelector('[name="id"]');
    if (!idInput) return;

    container.dataset.c_priceReady = 'true';

    const regular = priceEl.querySelector('[data-regular-price]');
    const sale = priceEl.querySelector('[data-sale-price]');

    const money = (cents) =>
      currency.formatMoney(Math.round(cents * factor), window.theme.moneyFormat);

    /*  同じ内容なら書き換えない。書き換えるとまた自分の監視に引っかかる  */
    const setMoney = (el, html) => {
      if (el && el.innerHTML.trim() !== html) el.innerHTML = html;
    };

    const apply = () => {
      const variant = productJson.variants.find(
        (item) => String(item.id) === idInput.value
      );
      if (!variant) return;

      const onSale = variant.compare_at_price > variant.price;

      setMoney(regular, money(onSale ? variant.compare_at_price : variant.price));
      setMoney(sale, money(variant.price));

      /*  自分が出した変更は監視対象から捨てる  */
      observer.takeRecords();
    };

    const observer = new MutationObserver((records) => {
      if (records.length) apply();
    });

    observer.observe(priceEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  const initPrices = (scope) => {
    scope.querySelectorAll(SECTION).forEach(initPrice);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPrices(document));
  } else {
    initPrices(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initPrices(event.target);
  });
})();

/* ==================================================
  カスタム:商品詳細のカラー切替

  カラー違いは別商品として登録されている。
  セレクターで選ばれたカラーの商品ページへ移動する
===================================================*/

(() => {
  const SELECT = '[data-c_pt-color-select]';

  const reset = (select) => {
    Array.from(select.options).forEach((option) => {
      option.selected = option.defaultSelected;
    });
  };

  const initSelect = (select) => {
    if (select.dataset.c_colorReady === 'true') return;
    select.dataset.c_colorReady = 'true';

    select.addEventListener('change', () => {
      if (!select.value) return;
      window.location.assign(select.value);
    });
  };

  const initSelects = (scope) => {
    scope.querySelectorAll(SELECT).forEach(initSelect);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSelects(document));
  } else {
    initSelects(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSelects(event.target);
  });

  /*  ブラウザの「戻る」で復元されると移動先のカラーが選ばれたままになるため、
      表示中の商品のカラーへ戻す  */
  window.addEventListener('pageshow', () => {
    document.querySelectorAll(SELECT).forEach(reset);
  });
})();
